import { useState, useEffect } from "react";
import { useForm, ValidationError } from "@formspree/react";
import TEAR_SHEETS_CONFIG from "./tearsheets.json";

function useMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

const C = {
  ink:    "#1e2130", ink2: "#242840", ink3: "#2a2f4a",
  gold:   "#c8913a", goldD: "rgba(200,145,58,0.12)", goldB: "rgba(200,145,58,0.28)",
  green:  "#34d399", greenD: "rgba(52,211,153,0.10)", greenB: "rgba(52,211,153,0.25)",
  blue:   "#5b8fd4", blueD: "rgba(91,143,212,0.12)",  blueB: "rgba(91,143,212,0.28)",
  white:  "#ece8e0", muted: "#7a8290", muted2: "#9ea8b8",
  border: "rgba(255,255,255,0.08)",
};
const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700&family=Outfit:wght@300;400;500&family=DM+Mono:wght@400;500&display=swap');`;
const mono    = { fontFamily:"'DM Mono',monospace" };
const display = { fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.04em" };
const body    = { fontFamily:"'Outfit',sans-serif", fontWeight:300, fontSize:18, lineHeight:1.75 };

function Label({ text, color }) {
  return (
    <div style={{ ...mono, fontSize:10, letterSpacing:"0.22em", textTransform:"uppercase",
      color:color||C.gold, display:"flex", alignItems:"center", gap:12, marginBottom:28 }}>
      <span style={{ width:20, height:1, background:color||C.gold, display:"inline-block", flexShrink:0 }}/>
      {text}
    </div>
  );
}
function H2({ children, style }) {
  return <h2 style={{ ...display, fontSize:"clamp(26px,3.2vw,42px)", lineHeight:1.05, color:C.white, marginBottom:16, ...style }}>{children}</h2>;
}
function GoldBtn({ label, href, onClick, color, border, dim }) {
  const s = { ...mono, fontSize:10, letterSpacing:"0.15em", textTransform:"uppercase",
    color, border:`1px solid ${border}`, padding:"10px 22px", background:"transparent",
    cursor:"pointer", textDecoration:"none", display:"inline-block", transition:"background 0.2s" };
  const over = e => e.currentTarget.style.background = dim;
  const out  = e => e.currentTarget.style.background = "transparent";
  if (href) return <a href={href} style={s} onMouseEnter={over} onMouseLeave={out}>{label}</a>;
  return <button onClick={onClick} style={s} onMouseEnter={over} onMouseLeave={out}>{label}</button>;
}

// ── NAV ──────────────────────────────────────────────
function Nav({ section, goHome, goFinance, goRevenue, goTearSheets }) {
  const isMobile = useMobile();
  const ac = section==="revenue" ? C.blue : section==="tearsheets" ? C.green : C.gold;
  const ab = section==="revenue" ? C.blueB : section==="tearsheets" ? C.greenB : C.goldB;
  const ad = section==="revenue" ? C.blueD : section==="tearsheets" ? C.greenD : C.goldD;

  const tabs = (
    <div style={{ display:"flex", gap:3, background:C.ink2, border:`1px solid ${C.border}`, padding:3 }}>
      {[{id:"finance",label:"Finance",ac:C.gold,ab:C.goldB,ad:C.goldD},
        {id:"revenue",label:"Revenue",ac:C.blue, ab:C.blueB, ad:C.blueD}].map(tab => {
        const active = section === tab.id;
        return (
          <button key={tab.id} onClick={tab.id==="finance"?goFinance:goRevenue}
            style={{ ...mono, fontSize:10, letterSpacing:"0.14em", textTransform:"uppercase",
              padding:"7px 16px", border:"none", cursor:"pointer",
              background:active?tab.ad:"transparent",
              color:active?tab.ac:C.muted,
              outline:active?`1px solid ${tab.ab}`:"none",
              transition:"all 0.2s" }}>
            {tab.label}
          </button>
        );
      })}
    </div>
  );

  const convBtn = (
    <a href="https://scheduler.zoom.us/jake-nelson-s24e55/30-mins-with-jake" target="_blank" rel="noreferrer"
      style={{ ...mono, fontSize:isMobile?9:10, letterSpacing:"0.14em", textTransform:"uppercase",
        color:ac, border:`1px solid ${ab}`, padding:isMobile?"6px 10px":"8px 16px",
        textDecoration:"none", transition:"background 0.2s", whiteSpace:"nowrap" }}
      onMouseEnter={e=>e.currentTarget.style.background=ad}
      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
      Start a Conversation
    </a>
  );

  if (isMobile) {
    return (
      <nav style={{ position:"fixed", top:0, left:0, right:0, zIndex:300,
        display:"flex", flexDirection:"column",
        padding:"8px 16px", gap:6,
        background:"rgba(20,22,32,0.97)", borderBottom:`1px solid ${C.border}`,
        backdropFilter:"blur(16px)" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <button onClick={goHome} style={{ ...display, fontSize:16, color:C.gold,
            background:"none", border:"none", cursor:"pointer", padding:0 }}>
            J2 Summit
          </button>
          {convBtn}
        </div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", width:"100%" }}>
          {tabs}
          {section !== "home" && (
            <button onClick={goHome} style={{ ...mono, fontSize:10, letterSpacing:"0.12em",
              textTransform:"uppercase", color:C.muted, background:"none", border:"none",
              cursor:"pointer", marginLeft:12 }}>← Home</button>
          )}
        </div>
      </nav>
    );
  }

  return (
    <nav style={{ position:"fixed", top:0, left:0, right:0, zIndex:300,
      display:"flex", alignItems:"center", justifyContent:"space-between",
      padding:"0 48px", height:58,
      background:"rgba(20,22,32,0.97)", borderBottom:`1px solid ${C.border}`,
      backdropFilter:"blur(16px)", gap:16 }}>
      <button onClick={goHome} style={{ ...display, fontSize:20, color:C.gold,
        background:"none", border:"none", cursor:"pointer", padding:0, flexShrink:0 }}>
        J2 Summit
      </button>
      {tabs}
      <div style={{ display:"flex", alignItems:"center", gap:12, flexShrink:0 }}>
        {section !== "home" && (
          <button onClick={goHome} style={{ ...mono, fontSize:10, letterSpacing:"0.12em",
            textTransform:"uppercase", color:C.muted, background:"none", border:"none",
            cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>← Home</button>
        )}
        {convBtn}
      </div>
    </nav>
  );
}

// ── FINANCE SUB-TABS ─────────────────────────────────
function FinSubTabs({ tab, setTab }) {
  const isMobile = useMobile();
  const tabs = [{id:"overview",label:"Overview"},{id:"services",label:"Services"},
    {id:"how",label:"How It Works"},{id:"about",label:"About Jake"},{id:"contact",label:"Contact"},
    {id:"tearsheets",label:"Tear Sheets"}];
  return (
    <div style={{ position:"fixed", top:isMobile?100:58, left:0, right:0, zIndex:290,
      background:C.ink3, borderBottom:`2px solid ${C.goldB}`,
      display:"flex", overflowX:"auto", WebkitOverflowScrolling:"touch",
      boxShadow:"0 2px 16px rgba(0,0,0,0.5)" }}>
      {tabs.map(t => (
        <button key={t.id} onClick={()=>setTab(t.id)}
          style={{ ...mono, fontSize:10, letterSpacing:"0.15em", textTransform:"uppercase",
            padding:"13px 22px", border:"none", flexShrink:0, cursor:"pointer",
            borderBottom:`3px solid ${tab===t.id?C.gold:"transparent"}`,
            background:tab===t.id?C.goldD:"transparent",
            color:tab===t.id?C.gold:C.muted2, transition:"all 0.15s" }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── HOME ──────────────────────────────────────────────
function HomePage({ goFinance, goRevenue }) {
  const isMobile = useMobile();
  return (
    <div style={{ minHeight:"100vh", background:C.ink, display:"flex",
      flexDirection:"column", justifyContent:"flex-end", position:"relative", overflow:"hidden", overflowX:"hidden" }}>
      <div style={{ position:"absolute", inset:0, background:
        `radial-gradient(ellipse 60% 50% at 80% 20%,rgba(200,145,58,0.06) 0%,transparent 60%),
         radial-gradient(ellipse 40% 40% at 10% 80%,rgba(200,145,58,0.03) 0%,transparent 55%),
         linear-gradient(160deg,#1e2130 0%,#242840 100%)` }} />
      <section style={{ position:"relative", zIndex:1, padding:isMobile?"0 20px 56px":"0 56px 88px" }}>
        <div style={{ ...mono, fontSize:11, letterSpacing:"0.25em", textTransform:"uppercase",
          color:C.gold, marginBottom:24, display:"flex", alignItems:"center", gap:14 }}>
          <span style={{ width:28, height:1, background:C.gold, display:"inline-block" }}/>
          J2 Summit
        </div>
        <h1 style={{ ...display, fontSize:isMobile?"clamp(26px,7vw,40px)":"clamp(44px,7vw,96px)", lineHeight:isMobile?1.2:0.93,
          letterSpacing:isMobile?"0.01em":"0.04em",
          color:C.white, marginBottom:36, maxWidth:"100%" }}>
          {isMobile ? (
            <>Operator-led advisory. <span style={{ color:C.gold }}>Built for firms where the work has to hold up.</span></>
          ) : (
            <>Operator-led advisory.<br/><span style={{ color:C.gold }}>Built for firms where</span><br/><span style={{ color:C.gold }}>the work has to hold up.</span></>
          )}
        </h1>
        <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:16, maxWidth:760 }}>
          {[{tag:"J2 Finance",title:"Trading, Risk & Market Infrastructure",
              sub:"Where trading, risk, and systems come together.",
              cta:"ex-PEAK6 · 20+ years →",
              color:C.gold, border:C.goldB, dim:C.goldD, action:goFinance},
            {tag:"J2 Revenue",title:"GTM, Revenue & Sales Infrastructure",
              sub:"Built for B2B companies ready to scale.",
              cta:"Operator-built GTM →",
              color:C.blue, border:C.blueB, dim:C.blueD, action:goRevenue}
          ].map(card => (
            <div key={card.tag} onClick={card.action}
              style={{ border:`1px solid ${card.border}`, padding:"36px 32px",
                cursor:"pointer", background:"transparent", transition:"background 0.2s" }}
              onMouseEnter={e=>e.currentTarget.style.background=card.dim}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <div style={{ ...mono, fontSize:10, letterSpacing:"0.18em", textTransform:"uppercase",
                color:card.color, marginBottom:10 }}>{card.tag}</div>
              <div style={{ ...display, fontSize:22, color:C.white, marginBottom:10,
                letterSpacing:"0.02em", lineHeight:1.15 }}>{card.title}</div>
              <div style={{ ...body, fontSize:16, color:C.muted2, marginBottom:20 }}>{card.sub}</div>
              <div style={{ ...mono, fontSize:11, color:card.color }}>{card.cta}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ── FINANCE OVERVIEW ─────────────────────────────────
function FinOverview({ setTab }) {
  const isMobile = useMobile();
  return (
    <div>
      <section style={{ padding:isMobile?"80px 20px 48px":"100px 56px 80px", background:C.ink, minHeight:"60vh",
        display:"flex", flexDirection:"column", justifyContent:"flex-end",
        position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, background:
          `radial-gradient(ellipse 55% 55% at 85% 15%,rgba(200,145,58,0.06) 0%,transparent 60%),
           linear-gradient(160deg,#1e2130 0%,#1e2130 100%)`, pointerEvents:"none" }}/>
        <div style={{ position:"relative", zIndex:1 }}>
          <Label text="Where trading, risk, and systems come together"/>
          <h1 style={{ ...display, fontSize:isMobile?"clamp(28px,8vw,44px)":"clamp(44px,7vw,92px)", lineHeight:isMobile?1.1:0.93,
            color:C.white, marginBottom:28, maxWidth:"100%" }}>
            Where trading,<br/>risk, and systems<br/><span style={{ color:C.gold }}>come together.</span>
          </h1>
          <p style={{ maxWidth:540, color:C.muted2, fontSize:18, lineHeight:1.75, marginBottom:44 }}>
            J2 works with trading firms and hedge funds to build the risk infrastructure, desk structure, and analytics systems that allow them to scale safely. Our team brings 20+ years of experience operating inside these environments — including over a decade at PEAK6, and across systematic, discretionary, and crypto derivatives after that.
          </p>
          <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
            <GoldBtn label="Talk to J2 Finance" href="mailto:info@j2summit.com"
              color={C.gold} border={C.goldB} dim={C.goldD}/>
            <button onClick={()=>setTab("services")}
              style={{ ...mono, fontSize:10, letterSpacing:"0.14em", textTransform:"uppercase",
                color:C.muted2, background:"none", border:"none", cursor:"pointer" }}>
              See Our Services →
            </button>
          </div>
        </div>
      </section>
      <div style={{ borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}`,
        padding:"12px 0", overflow:"hidden", background:C.ink2 }}>
        <div style={{ display:"flex", animation:"marquee 36s linear infinite", width:"max-content" }}>
          {[1,2].map(k=>(
            <div key={k} style={{ ...mono, fontSize:10, letterSpacing:"0.18em", textTransform:"uppercase",
              color:C.muted, padding:"0 48px", whiteSpace:"nowrap",
              display:"flex", alignItems:"center", gap:40 }}>
              Trading Desk Build-Outs <span style={{ color:C.gold, fontSize:7 }}>◆</span> Risk Framework Design <span style={{ color:C.gold, fontSize:7 }}>◆</span> Exposure Monitoring <span style={{ color:C.gold, fontSize:7 }}>◆</span> Stress Testing <span style={{ color:C.gold, fontSize:7 }}>◆</span> Python Analytics <span style={{ color:C.gold, fontSize:7 }}>◆</span> Governance & Controls
            </div>
          ))}
        </div>
      </div>
      <section style={{ padding:isMobile?"56px 20px":"96px 56px", background:C.ink2 }}>
        <Label text="When Firms Call Us"/>
        <H2>Four problems. One operator.</H2>
        <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:1, background:C.border, marginTop:40 }}>
          {[{n:"01",title:"Launching or Scaling a Desk",body:"You're building a trading operation and need the right structure, controls, and operating model from day one — not retrofitted later when it's expensive."},
            {n:"02",title:"Risk Management Gaps",body:"Exposure monitoring is reactive, governance is informal, or a risk event made it clear the framework isn't built for your current scale."},
            {n:"03",title:"Outgrowing Your Systems",body:"Analytics are manual, reporting is slow, and your infrastructure no longer supports the pace or complexity of your current strategy."},
            {n:"04",title:"Automation & Decision Speed",body:"You need tools that give traders better, faster information — automated workflows, real-time monitoring, and analytics that actually get used."},
          ].map(p=>(
            <div key={p.n} style={{ background:C.ink2, padding:"44px 40px", transition:"background 0.2s" }}
              onMouseEnter={e=>e.currentTarget.style.background=C.ink3}
              onMouseLeave={e=>e.currentTarget.style.background=C.ink2}>
              <div style={{ ...mono, fontSize:11, color:C.gold, letterSpacing:"0.2em", marginBottom:14 }}>{p.n}</div>
              <h3 style={{ ...display, fontSize:22, color:C.white, marginBottom:12 }}>{p.title}</h3>
              <p style={{ ...body, fontSize:17, color:C.muted2 }}>{p.body}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ── FINANCE SERVICES ─────────────────────────────────
function FinServices() {
  const isMobile = useMobile();
  return (
    <section style={{ padding:isMobile?"56px 20px":"96px 56px", background:C.ink }}>
      <Label text="What We Do"/>
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:isMobile?24:48, marginBottom:56 }}>
        <H2>Three service lines.</H2>
        <p style={{ ...body, fontSize:17, color:C.muted2 }}>Every engagement is built around your specific situation. No generic playbooks, no junior teams. Senior operator experience applied directly — and we stay until the work is done.</p>
      </div>
      {[{n:"01",title:"Trading Desk Strategy & Build",items:["Design desk structure, roles and decision workflows","Build scalable operating models for multi-strategy teams","Establish trade review, attribution and performance frameworks","Support new launches, restructurings and team scaling"]},
        {n:"02",title:"Risk Frameworks",items:["Design exposure monitoring and control frameworks","Implement limits, escalation protocols and daily risk reporting","Run stress testing, scenario analysis and tail risk reviews","Build governance processes that scale with growth"]},
        {n:"03",title:"Trading Systems & Analytics",items:["Evaluate and improve trading infrastructure and data pipelines","Build analytics and monitoring tools","Automate reporting, reconciliation and operational workflows","Enhance execution and pre/post-trade analysis"]},
      ].map(svc=>(
        <div key={svc.n} style={{ borderTop:`1px solid ${C.border}`, padding:"48px 0",
          display:"grid", gridTemplateColumns:isMobile?"1fr":"220px 1fr", gap:isMobile?24:48 }}>
          <div>
            <div style={{ ...mono, fontSize:11, color:C.gold, letterSpacing:"0.2em", marginBottom:10 }}>{svc.n} / Service</div>
            <div style={{ ...display, fontSize:28, color:C.white, lineHeight:1.05 }}>{svc.title}</div>
          </div>
          <ul style={{ listStyle:"none", display:"flex", flexDirection:"column", gap:12 }}>
            {svc.items.map(item=>(
              <li key={item} style={{ ...body, fontSize:17, color:C.muted2, paddingLeft:22, position:"relative", lineHeight:1.6 }}>
                <span style={{ position:"absolute", left:0, color:C.gold, ...mono }}>—</span>{item}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

// ── FINANCE HOW ───────────────────────────────────────
function FinHow() {
  const isMobile = useMobile();
  return (
    <div>
      <section style={{ padding:isMobile?"56px 20px":"96px 56px", background:C.ink2 }}>
        <Label text="Engagement Model"/>
        <H2>How it works.</H2>
        <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)", gap:1, background:C.border, marginTop:40 }}>
          {[{n:"Step 01",title:"Diagnostic Call",body:"A focused 30-minute conversation to understand your firm's structure, the specific problem, and whether there's a fit."},
            {n:"Step 02",title:"Scoped Proposal",body:"Clear deliverables, timeline, and engagement structure. No ambiguity on scope or cost before we start."},
            {n:"Step 03",title:"Embedded Work",body:"Senior operator directly inside your environment. Gap identification is fast. No hand-off to a junior team."},
            {n:"Step 04",title:"Operational Handoff",body:"Work is documented, trained, and owned by your team. Ongoing advisory available for the next phase."},
          ].map(s=>(
            <div key={s.n} style={{ background:C.ink2, padding:"40px 28px" }}>
              <div style={{ ...mono, fontSize:11, color:C.gold, letterSpacing:"0.2em", marginBottom:18 }}>{s.n}</div>
              <h3 style={{ ...display, fontSize:22, color:C.white, marginBottom:12 }}>{s.title}</h3>
              <p style={{ ...body, fontSize:16, color:C.muted }}>{s.body}</p>
            </div>
          ))}
        </div>
      </section>
      <section style={{ padding:isMobile?"56px 20px":"96px 56px", background:C.ink }}>
        <Label text="Who We Serve"/>
        <H2>We work with firms where disciplined risk-taking drives performance.</H2>
        <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr 1fr", gap:16, marginTop:40 }}>
          {[{title:"Hedge Funds & Prop Trading Firms",body:"Multi-strategy funds and prop shops scaling complexity. Strengthening risk infrastructure, analytics, and operating discipline as the business grows."},
            {title:"Banks & Institutional Desks",body:"Trading desks addressing gaps in governance, systems, and workflow efficiency. Built to operate within structured, regulated environments."},
            {title:"Family Offices with Trading Operations",body:"Active trading teams that need institutional-grade risk and infrastructure without building a full internal organization."},
            {title:"Crypto & Digital Asset Platforms",body:"Exchanges and digital asset firms building toward institutional standards. Applying proven trading and risk discipline to rapidly evolving markets."},
            {title:"Fintech & Trading-Focused Startups",body:"Early and growth-stage platforms where trading, payments, or financial infrastructure is core. Building scalable risk frameworks from the ground up."},
          ].map(c=>(
            <div key={c.title} style={{ border:`1px solid ${C.border}`, padding:"32px 28px", transition:"all 0.2s" }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=C.goldB;e.currentTarget.style.background=C.goldD;}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.background="transparent";}}>
              <h3 style={{ ...display, fontSize:20, color:C.white, marginBottom:10 }}>{c.title}</h3>
              <p style={{ ...body, fontSize:16, color:C.muted }}>{c.body}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ── FINANCE ABOUT ────────────────────────────────────
function FinAbout() {
  const isMobile = useMobile();
  return (
    <section style={{ padding:isMobile?"56px 20px":"96px 56px", background:C.ink }}>
      <Label text="Who We Are"/>
      <H2>The operator behind the work.</H2>
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"260px 1fr", gap:isMobile?32:64, marginTop:48 }}>
        <div style={{ width:"100%", aspectRatio:"3/4", background:C.ink2, border:`1px solid ${C.border}`,
          display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
          position:"relative", gap:12 }}>
          <div style={{ width:64, height:64, borderRadius:"50%", background:C.goldD,
            border:`1px solid ${C.goldB}`, display:"flex", alignItems:"center", justifyContent:"center",
            ...display, fontSize:22, color:C.gold }}>JN</div>
          <div style={{ position:"absolute", bottom:0, left:0, right:0,
            background:"rgba(26,28,36,0.94)", borderTop:`1px solid ${C.goldB}`,
            padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div>
              <div style={{ ...display, fontSize:18, color:C.white }}>Jake Nelson</div>
              <div style={{ ...mono, fontSize:10, color:C.gold, letterSpacing:"0.12em", textTransform:"uppercase", marginTop:2 }}>Founder & CEO</div>
            </div>
            <a href="https://linkedin.com/in/jake-nelson-" target="_blank" rel="noreferrer"
              style={{ ...mono, fontSize:10, color:C.muted, textDecoration:"none",
                border:`1px solid ${C.border}`, padding:"5px 10px",
                letterSpacing:"0.1em", textTransform:"uppercase" }}>LinkedIn ↗</a>
          </div>
        </div>
        <div>
          <div style={{ ...display, fontSize:80, color:C.gold, opacity:0.08, lineHeight:1, marginBottom:-16 }}>20+</div>
          <p style={{ ...body, fontSize:20, color:C.white, lineHeight:1.65, marginBottom:22 }}>
            <strong style={{ color:C.gold }}>Most consulting firms analyze trading businesses from the outside.</strong> I spent 20+ years operating inside them.
          </p>
          <p style={{ ...body, fontSize:17, color:C.muted2, marginBottom:20 }}>I've run trading books, built risk infrastructure, onboarded traders, and worked directly with executive leadership on firm-wide exposure. That perspective allows J2 Finance to deliver practical solutions that work inside real trading environments — not theoretical frameworks.</p>
          <p style={{ ...body, fontSize:17, color:C.muted2, marginBottom:20 }}>Jake joined PEAK6 in its early years and became one of its longest-tenured operators — rising from the trading desk through senior risk management at one of the most respected options market-making firms ever built.</p>
          <p style={{ ...body, fontSize:17, color:C.muted2, marginBottom:32 }}>After PEAK6: portfolio management at a multi-strategy fund, discretionary portfolio strategy at a proprietary investment firm, and consulting for a crypto exchange platform.</p>
          <div style={{ borderTop:`1px solid ${C.border}` }}>
            {[["Foundation","PEAK6 — early team, long-tenured, from trading desk to senior risk management"],
              ["Experience","Portfolio management · Discretionary portfolio strategy · Crypto exchange platform"],
              ["Markets","Equities, Derivatives, Commodities, Crypto"],
              ["Approach","Embedded, senior-led — no hand-off to a junior team"],
            ].map(([k,v])=>(
              <div key={k} style={{ display:"flex", gap:20, padding:"13px 0", borderBottom:`1px solid ${C.border}` }}>
                <span style={{ ...mono, fontSize:10, letterSpacing:"0.15em", textTransform:"uppercase",
                  color:C.muted, minWidth:100, paddingTop:3 }}>{k}</span>
                <span style={{ ...body, fontSize:16, color:C.white, lineHeight:1.5 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── FINANCE CONTACT ───────────────────────────────────
function FinContact() {
  const isMobile = useMobile();
  const [state, handleSubmit] = useForm("mykbpbyr");
  const inp = { background:C.ink, border:`1px solid ${C.border}`, color:C.white,
    padding:"11px 14px", fontFamily:"'Outfit',sans-serif", fontSize:16,
    outline:"none", width:"100%", colorScheme:"dark" };
  return (
    <section style={{ padding:isMobile?"56px 20px":"96px 56px", background:C.ink2 }}>
      <Label text="Get In Touch"/>
      <H2>Start the conversation.</H2>
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:isMobile?32:72, marginTop:48 }}>
        <div>
          <h3 style={{ ...display, fontSize:22, color:C.white, marginBottom:16 }}>What to Expect</h3>
          <p style={{ ...body, fontSize:17, color:C.muted2, marginBottom:14 }}>Every inquiry is reviewed personally by Jake. If there's a fit, we'll schedule a focused diagnostic call within 48 hours.</p>
          <p style={{ ...body, fontSize:17, color:C.muted2, marginBottom:28 }}>J2 Finance works with a small number of clients at a time — every engagement gets senior attention throughout.</p>
          {[["Email","info@j2summit.com","mailto:info@j2summit.com"],
            ["LinkedIn","Jake Nelson","https://linkedin.com/in/jake-nelson-"],
            ["Book a Call","30 mins with Jake","https://scheduler.zoom.us/jake-nelson-s24e55/30-mins-with-jake"],
            ["Based In","Denver, CO · New York, NY · San Francisco, CA · Remote",null]].map(([l,v,href])=>(
            <div key={l} style={{ display:"flex", alignItems:"center", gap:16, marginBottom:14 }}>
              <span style={{ ...mono, fontSize:10, letterSpacing:"0.2em", textTransform:"uppercase",
                color:C.muted, width:72, flexShrink:0 }}>{l}</span>
              {href?<a href={href} target={href.startsWith("http")?"_blank":undefined} rel="noreferrer"
                style={{ ...body, fontSize:16, color:C.white, textDecoration:"none" }}>{v}</a>
               :<span style={{ ...body, fontSize:16, color:C.white }}>{v}</span>}
            </div>
          ))}
        </div>
        {state.succeeded?(
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
            border:"1px solid rgba(52,211,153,0.3)", background:"rgba(52,211,153,0.05)", padding:48, gap:16 }}>
            <div style={{ fontSize:32, color:"#34d399" }}>✓</div>
            <div style={{ ...display, fontSize:22, color:C.white }}>Message Sent</div>
            <p style={{ ...body, color:C.muted2, textAlign:"center" }}>Jake will be in touch within 48 hours.</p>
          </div>
        ):(
          <form style={{ display:"flex", flexDirection:"column", gap:14 }} onSubmit={handleSubmit}>
            {[["Your Name","name","text","Full name"],["Firm Name","firm","text","Company or fund"],["Email","email","email","you@firm.com"]].map(([label,field,type,ph])=>(
              <div key={field} style={{ display:"flex", flexDirection:"column", gap:6 }}>
                <label style={{ ...mono, fontSize:10, letterSpacing:"0.2em", textTransform:"uppercase", color:C.muted }}>{label}</label>
                <input type={type} name={field} placeholder={ph} required style={inp}/>
                <ValidationError field={field} errors={state.errors}
                  style={{ ...mono, fontSize:11, color:"#f87171" }}/>
              </div>
            ))}
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              <label style={{ ...mono, fontSize:10, letterSpacing:"0.2em", textTransform:"uppercase", color:C.muted }}>Topic</label>
              <select name="topic" style={inp}>
                <option value="">Select a topic</option>
                <option>Trading Desk Strategy & Build</option>
                <option>Risk Framework Design</option>
                <option>Trading Systems & Analytics</option>
                <option>Something else</option>
              </select>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              <label style={{ ...mono, fontSize:10, letterSpacing:"0.2em", textTransform:"uppercase", color:C.muted }}>Brief Description</label>
              <textarea name="message" placeholder="What are you working on or trying to solve?" rows={4}
                style={{ ...inp, resize:"vertical" }}/>
              <ValidationError field="message" errors={state.errors}
                style={{ ...mono, fontSize:11, color:"#f87171" }}/>
            </div>
            {state.errors && state.errors.length > 0 && !state.succeeded &&
              <p style={{ ...mono, fontSize:11, color:"#f87171" }}>Something went wrong — please email info@j2summit.com directly.</p>}
            <button type="submit" disabled={state.submitting}
              style={{ ...mono, fontSize:10, letterSpacing:"0.15em", textTransform:"uppercase",
                color:C.gold, background:"transparent", border:`1px solid ${C.goldB}`,
                padding:"12px 24px", cursor:"pointer", alignSelf:"flex-start",
                opacity:state.submitting?0.6:1 }}>
              {state.submitting ? "Sending..." : "Send Message →"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

// ── REVENUE SUB-TABS ─────────────────────────────────
function RevSubTabs({ tab, setTab }) {
  const isMobile = useMobile();
  const tabs = [{id:"overview",label:"Overview"},{id:"services",label:"Services"},
    {id:"how",label:"How It Works"},{id:"contact",label:"Contact"}];
  return (
    <div style={{ position:"fixed", top:isMobile?100:58, left:0, right:0, zIndex:290,
      background:C.ink3, borderBottom:`2px solid ${C.blueB}`,
      display:"flex", overflowX:"auto", WebkitOverflowScrolling:"touch",
      boxShadow:"0 2px 16px rgba(0,0,0,0.5)" }}>
      {tabs.map(t => (
        <button key={t.id} onClick={()=>setTab(t.id)}
          style={{ ...mono, fontSize:10, letterSpacing:"0.15em", textTransform:"uppercase",
            padding:"13px 22px", border:"none", flexShrink:0, cursor:"pointer",
            borderBottom:`3px solid ${tab===t.id?C.blue:"transparent"}`,
            background:tab===t.id?C.blueD:"transparent",
            color:tab===t.id?C.blue:C.muted2, transition:"all 0.15s" }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── REVENUE OVERVIEW ─────────────────────────────────
function RevOverview({ setTab }) {
  const isMobile = useMobile();
  return (
    <div>
      <section style={{ padding:isMobile?"80px 20px 48px":"100px 56px 80px", background:C.ink, minHeight:"60vh",
        display:"flex", flexDirection:"column", justifyContent:"flex-end",
        position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, background:
          `radial-gradient(ellipse 55% 55% at 85% 15%,rgba(91,143,212,0.06) 0%,transparent 60%),
           linear-gradient(160deg,#1e2130 0%,#1e2130 100%)`, pointerEvents:"none" }}/>
        <div style={{ position:"relative", zIndex:1 }}>
          <Label text="J2 Revenue · GTM & Sales Infrastructure" color={C.blue}/>
          <h1 style={{ ...display, fontSize:isMobile?"clamp(28px,8vw,44px)":"clamp(44px,7vw,88px)", lineHeight:isMobile?1.1:0.93,
            color:C.white, marginBottom:28, maxWidth:"100%" }}>
            Revenue doesn't<br/>scale by accident.<br/><span style={{ color:C.blue }}>We build the engine.</span>
          </h1>
          <p style={{ maxWidth:540, color:C.muted2, fontSize:18, lineHeight:1.75, marginBottom:44 }}>
            B2B companies don't stall because the product is wrong. They stall because the revenue engine isn't built. J2 Revenue designs, builds, and runs the GTM infrastructure that turns early traction into a repeatable sales machine.
          </p>
          <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
            <GoldBtn label="Talk to J2 Revenue" href="mailto:info@j2summit.com"
              color={C.blue} border={C.blueB} dim={C.blueD}/>
            <button onClick={()=>setTab("services")}
              style={{ ...mono, fontSize:10, letterSpacing:"0.14em", textTransform:"uppercase",
                color:C.muted2, background:"none", border:"none", cursor:"pointer" }}>
              See Our Services →
            </button>
          </div>
        </div>
      </section>
      <div style={{ borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}`,
        padding:"12px 0", overflow:"hidden", background:C.ink2 }}>
        <div style={{ display:"flex", animation:"marquee 36s linear infinite", width:"max-content" }}>
          {[1,2].map(k=>(
            <div key={k} style={{ ...mono, fontSize:10, letterSpacing:"0.18em", textTransform:"uppercase",
              color:C.muted, padding:"0 48px", whiteSpace:"nowrap",
              display:"flex", alignItems:"center", gap:40 }}>
              GTM Strategy <span style={{ color:C.blue, fontSize:7 }}>◆</span> SDR Build-Out <span style={{ color:C.blue, fontSize:7 }}>◆</span> Sales Playbooks <span style={{ color:C.blue, fontSize:7 }}>◆</span> AI SDR <span style={{ color:C.blue, fontSize:7 }}>◆</span> CRM & RevOps <span style={{ color:C.blue, fontSize:7 }}>◆</span> Fractional CRO
            </div>
          ))}
        </div>
      </div>
      <section style={{ padding:isMobile?"56px 20px":"96px 56px", background:C.ink2 }}>
        <Label text="Why Firms Call Us" color={C.blue}/>
        <H2>The revenue engine is always the bottleneck.</H2>
        <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:1, background:C.border, marginTop:40 }}>
          {[{n:"01",title:"No Repeatable Outbound",body:"You're relying on inbound, referrals, or founder-led sales. It's working at small scale but won't hold as you grow."},
            {n:"02",title:"SDR Team Isn't Ramping",body:"You've hired reps but conversion is low, messaging is inconsistent, and nobody owns the playbook."},
            {n:"03",title:"CRM Is a Mess",body:"Pipeline data is unreliable, forecasting is guesswork, and your rev ops infrastructure isn't built for the team you have today."},
            {n:"04",title:"GTM Is Undefined",body:"You have a product and early customers but no documented go-to-market motion. Scaling means building the foundation first."},
          ].map(p=>(
            <div key={p.n} style={{ background:C.ink2, padding:"44px 40px", transition:"background 0.2s" }}
              onMouseEnter={e=>e.currentTarget.style.background=C.ink3}
              onMouseLeave={e=>e.currentTarget.style.background=C.ink2}>
              <div style={{ ...mono, fontSize:11, color:C.blue, letterSpacing:"0.2em", marginBottom:14 }}>{p.n}</div>
              <h3 style={{ ...display, fontSize:22, color:C.white, marginBottom:12 }}>{p.title}</h3>
              <p style={{ ...body, fontSize:17, color:C.muted2 }}>{p.body}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ── REVENUE SERVICES ─────────────────────────────────
function RevServices() {
  const isMobile = useMobile();
  return (
    <section style={{ padding:isMobile?"56px 20px":"96px 56px", background:C.ink }}>
      <Label text="What We Do" color={C.blue}/>
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:isMobile?24:48, marginBottom:56 }}>
        <H2>Six service lines.</H2>
        <p style={{ ...body, fontSize:17, color:C.muted2 }}>Every engagement is built around your specific GTM challenge. No generic playbooks handed off to a junior team. Senior operators build and deliver directly.</p>
      </div>
      {[{n:"01",title:"GTM Strategy",items:["Define ICP and go-to-market motion","Build messaging, positioning, and outbound playbook","Design full sales process from first touch to close"]},
        {n:"02",title:"SDR Build-Out & Training",items:["Hire, onboard, and ramp SDR teams","Build outbound sequences, cadences, and scripts","Train reps on objection handling and qualification"]},
        {n:"03",title:"Sales Process & Playbooks",items:["Document full sales motion from prospecting to close","Build call frameworks, discovery scripts, and follow-up templates","Create repeatable processes your team can own and scale"]},
        {n:"04",title:"AI SDR Implementation",items:["Deploy AI-powered outreach and research tools","Automate prospecting and personalization at scale","Integrate with your existing CRM and tech stack"]},
        {n:"05",title:"CRM & RevOps",items:["Audit and optimize your CRM setup","Build pipeline reporting and revenue dashboards","Align sales and marketing data and workflows"]},
        {n:"06",title:"Fractional CRO",items:["Part-time senior revenue leadership","Own the full GTM strategy and execution","Coach and develop your internal team"]},
      ].map(svc=>(
        <div key={svc.n} style={{ borderTop:`1px solid ${C.border}`, padding:"48px 0",
          display:"grid", gridTemplateColumns:isMobile?"1fr":"220px 1fr", gap:isMobile?24:48 }}>
          <div>
            <div style={{ ...mono, fontSize:11, color:C.blue, letterSpacing:"0.2em", marginBottom:10 }}>{svc.n} / Service</div>
            <div style={{ ...display, fontSize:28, color:C.white, lineHeight:1.05 }}>{svc.title}</div>
          </div>
          <ul style={{ listStyle:"none", display:"flex", flexDirection:"column", gap:12 }}>
            {svc.items.map(item=>(
              <li key={item} style={{ ...body, fontSize:17, color:C.muted2, paddingLeft:22, position:"relative", lineHeight:1.6 }}>
                <span style={{ position:"absolute", left:0, color:C.blue, ...mono }}>—</span>{item}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

// ── REVENUE HOW ───────────────────────────────────────
function RevHow() {
  const isMobile = useMobile();
  return (
    <section style={{ padding:isMobile?"56px 20px":"96px 56px", background:C.ink2 }}>
      <Label text="Engagement Model" color={C.blue}/>
      <H2>How it works.</H2>
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)", gap:1, background:C.border, marginTop:40 }}>
        {[{n:"Step 01",title:"Revenue Diagnostic",body:"A no-cost 30-minute session to identify exactly where your GTM is breaking down."},
          {n:"Step 02",title:"Scoped Engagement",body:"Clear deliverables and timeline. Full-build, playbook only, or SDR coaching — you choose the scope."},
          {n:"Step 03",title:"Build & Deploy",body:"We build the GTM infrastructure directly. No hand-off to a junior team."},
          {n:"Step 04",title:"Handoff & Scale",body:"Your team owns the playbook. We stay available for the next phase."},
        ].map(s=>(
          <div key={s.n} style={{ background:C.ink2, padding:"40px 28px" }}>
            <div style={{ ...mono, fontSize:11, color:C.blue, letterSpacing:"0.2em", marginBottom:18 }}>{s.n}</div>
            <h3 style={{ ...display, fontSize:22, color:C.white, marginBottom:12 }}>{s.title}</h3>
            <p style={{ ...body, fontSize:16, color:C.muted }}>{s.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── REVENUE CONTACT ───────────────────────────────────
function RevContact() {
  const isMobile = useMobile();
  const [state, handleSubmit] = useForm("xvzvqvzk");
  const inp = { background:C.ink, border:`1px solid ${C.border}`, color:C.white,
    padding:"11px 14px", fontFamily:"'Outfit',sans-serif", fontSize:16,
    outline:"none", width:"100%", colorScheme:"dark" };
  return (
    <section style={{ padding:isMobile?"56px 20px":"96px 56px", background:C.ink }}>
      <Label text="Get In Touch" color={C.blue}/>
      <H2>Let's build your pipeline.</H2>
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:isMobile?32:72, marginTop:48 }}>
        <div>
          <p style={{ ...body, fontSize:17, color:C.muted2, marginBottom:14 }}>Complete the form and we'll be in touch within 24 hours. If there's a fit, we start with a no-cost revenue diagnostic.</p>
          <p style={{ ...body, fontSize:17, color:C.muted2, marginBottom:28 }}>J2 Revenue works with a focused set of clients at a time — every engagement gets senior attention throughout.</p>
          {[["Email","info@j2summit.com","mailto:info@j2summit.com"],
            ["Book a Call","30 mins with Jake","https://scheduler.zoom.us/jake-nelson-s24e55/30-mins-with-jake"],
            ["Based In","Denver, CO · New York, NY · San Francisco, CA · Remote",null]].map(([l,v,href])=>(
            <div key={l} style={{ display:"flex", alignItems:"center", gap:16, marginBottom:14 }}>
              <span style={{ ...mono, fontSize:10, letterSpacing:"0.2em", textTransform:"uppercase",
                color:C.muted, width:72, flexShrink:0 }}>{l}</span>
              {href?<a href={href} target="_blank" rel="noreferrer"
                style={{ ...body, fontSize:16, color:C.white, textDecoration:"none" }}>{v}</a>
                   :<span style={{ ...body, fontSize:16, color:C.white }}>{v}</span>}
            </div>
          ))}
        </div>
        {state.succeeded?(
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
            border:"1px solid rgba(52,211,153,0.3)", background:"rgba(52,211,153,0.05)", padding:48, gap:16 }}>
            <div style={{ fontSize:32, color:"#34d399" }}>✓</div>
            <div style={{ ...display, fontSize:22, color:C.white }}>Message Sent</div>
            <p style={{ ...body, color:C.muted2, textAlign:"center" }}>We'll be in touch within 24 hours.</p>
          </div>
        ):(
          <form style={{ display:"flex", flexDirection:"column", gap:14 }} onSubmit={handleSubmit}>
            {[["Name","name","text","Full name"],["Company","firm","text","Your startup"],["Email","email","email","you@company.com"]].map(([label,field,type,ph])=>(
              <div key={field} style={{ display:"flex", flexDirection:"column", gap:6 }}>
                <label style={{ ...mono, fontSize:10, letterSpacing:"0.2em", textTransform:"uppercase", color:C.muted }}>{label}</label>
                <input type={type} name={field} placeholder={ph} required style={inp}/>
                <ValidationError field={field} errors={state.errors}
                  style={{ ...mono, fontSize:11, color:"#f87171" }}/>
              </div>
            ))}
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              <label style={{ ...mono, fontSize:10, letterSpacing:"0.2em", textTransform:"uppercase", color:C.muted }}>Stage</label>
              <select name="stage" style={inp}>
                <option value="">Select stage</option>
                <option>Pre-seed / Seed</option>
                <option>Series A</option>
                <option>Series B</option>
                <option>Growth / Later Stage</option>
              </select>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              <label style={{ ...mono, fontSize:10, letterSpacing:"0.2em", textTransform:"uppercase", color:C.muted }}>Brief Description</label>
              <textarea name="message" placeholder="What's your biggest revenue challenge right now?" rows={4}
                style={{ ...inp, resize:"vertical" }}/>
              <ValidationError field="message" errors={state.errors}
                style={{ ...mono, fontSize:11, color:"#f87171" }}/>
            </div>
            {state.errors && state.errors.length > 0 && !state.succeeded &&
              <p style={{ ...mono, fontSize:11, color:"#f87171" }}>Something went wrong — please email info@j2summit.com directly.</p>}
            <button type="submit" disabled={state.submitting}
              style={{ ...mono, fontSize:10, letterSpacing:"0.15em", textTransform:"uppercase",
                color:C.blue, background:"transparent", border:`1px solid ${C.blueB}`,
                padding:"12px 24px", cursor:"pointer", alignSelf:"flex-start",
                opacity:state.submitting?0.6:1 }}>
              {state.submitting ? "Sending..." : "Send Message →"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}


// ── TEAR SHEETS PAGE ─────────────────────────────────
// Tear sheets loaded from src/tearsheets.json — edit that file to add/remove sheets
const TEAR_SHEETS = TEAR_SHEETS_CONFIG.map(ts => ({
  ...ts,
  color:  ts.color === "blue" ? C.blue  : C.gold,
  border: ts.color === "blue" ? "rgba(91,143,212,0.28)"  : "rgba(200,145,58,0.28)",
  dim:    ts.color === "blue" ? "rgba(91,143,212,0.12)"  : "rgba(200,145,58,0.12)",
}));

function TearSheetsPage() {
  const isMobile = useMobile();
  const SESSION_KEY = "j2_ts_unlocked";

  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(SESSION_KEY) === "true");
  const [userName, setUserName] = useState(() => sessionStorage.getItem("j2_ts_name") || "");
  const [nameField, setNameField] = useState("");
  const [tsState, tsSubmit] = useForm("xnjwdkkl");

  useEffect(() => {
    if (tsState.succeeded && !unlocked) {
      sessionStorage.setItem(SESSION_KEY, "true");
      const first = nameField.trim().split(" ")[0];
      sessionStorage.setItem("j2_ts_name", first);
      setUserName(first);
      setUnlocked(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tsState.succeeded]);


  const inp = {
    background: C.ink, border: "1px solid " + C.border, color: C.white,
    padding: "11px 14px", fontFamily: "'Outfit',sans-serif", fontSize: 16,
    outline: "none", width: "100%", colorScheme: "dark",
  };

  return (
    <div style={{ background: C.ink, minHeight: "100vh" }}>
      {/* Hero */}
      <section style={{ padding: isMobile?"60px 20px 40px":"80px 56px 48px", background: C.ink,
        borderBottom: "1px solid " + C.border }}>
        <div style={{ ...mono, fontSize:10, letterSpacing:"0.22em", textTransform:"uppercase",
          color: C.green, display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
          <span style={{ width:20, height:1, background:C.green, display:"inline-block" }}/>
          Sector Tear Sheets
        </div>
        <h1 style={{ ...display, fontSize: isMobile?"clamp(28px,8vw,44px)":"clamp(36px,5vw,60px)",
          lineHeight:1.0, color:C.white, marginBottom:16, maxWidth:700 }}>
          Capabilities by<br/><span style={{ color:C.green }}>sector.</span>
        </h1>
        <p style={{ ...body, fontSize:17, color:C.muted2, maxWidth:520 }}>
          Single-page capability overviews for each market J2 Summit serves. Built for sharing with your team or introducing J2 to a specific firm.
        </p>
      </section>

      {!unlocked ? (
        /* ── GATE ── */
        <section style={{ padding: isMobile?"40px 20px":"64px 56px", background: C.ink2 }}>
          <div style={{ maxWidth:560 }}>
            <div style={{ ...display, fontSize:22, color:C.white, marginBottom:8 }}>Access the Tear Sheets</div>
            <p style={{ ...body, fontSize:16, color:C.muted2, marginBottom:32 }}>
              Enter your details below for instant access to all sector tear sheets.
            </p>
            <form style={{ display:"flex", flexDirection:"column", gap:14 }} onSubmit={tsSubmit}>
              {[
                ["Your Name","name","text","Full name"],
                ["Email","email","email","you@firm.com"],
                ["Title","title","text","Your role"],
                ["Industry","industry","text","e.g. Hedge Fund, Fintech, Crypto"],
              ].map(([label, field, type, ph]) => (
                <div key={field} style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  <label style={{ ...mono, fontSize:10, letterSpacing:"0.2em",
                    textTransform:"uppercase", color:C.muted }}>{label}</label>
                  <input type={type} name={field} placeholder={ph} required style={inp}
                    onChange={field==="name" ? e=>setNameField(e.target.value) : undefined}/>
                  <ValidationError field={field} errors={tsState.errors} style={{fontFamily:"'DM Mono',monospace",fontSize:11,color:"#f87171"}}/>
                </div>
              ))}
              {tsState.errors && tsState.errors.length > 0 && <p style={{fontFamily:"'DM Mono',monospace",fontSize:11,color:"#f87171"}}>Something went wrong — please email jake@j2summit.com directly.</p>}
              <button type="submit" disabled={tsState.submitting} style={{
                fontFamily:"'DM Mono',monospace", fontSize:10, letterSpacing:"0.15em", textTransform:"uppercase",
                color: C.green, background:"transparent",
                border: "1px solid rgba(52,211,153,0.28)",
                padding:"12px 24px", cursor:"pointer", alignSelf:"flex-start",
                opacity: tsState.submitting ? 0.6 : 1,
              }}>
                {tsState.submitting ? "Submitting..." : "Access Tear Sheets →"}
              </button>
            </form>
          </div>
        </section>
      ) : (
        /* ── TEAR SHEET GRID ── */
        <section style={{ padding: isMobile?"32px 20px":"56px 56px" }}>
          {/* Welcome message */}
          <div style={{ background:C.ink3, borderLeft:"3px solid " + C.green,
            padding:"16px 20px", marginBottom:40, maxWidth:600 }}>
            <div style={{ ...mono, fontSize:11, color:C.green, letterSpacing:"0.1em",
              textTransform:"uppercase", marginBottom:4 }}>Welcome{userName ? `, ${userName}` : ""}</div>
            <p style={{ ...body, fontSize:15, color:C.muted2 }}>
              You now have access to all J2 Summit sector tear sheets. Click any card to open it.
            </p>
          </div>

          {/* Grid */}
          <div style={{ display:"grid",
            gridTemplateColumns: isMobile?"1fr":"1fr 1fr",
            gap:16, maxWidth:860 }}>
            {TEAR_SHEETS.map(ts => (
              <a key={ts.id} href={ts.url} target="_blank" rel="noreferrer"
                style={{ textDecoration:"none" }}>
                <div style={{ border:"1px solid " + ts.border, padding:"28px 24px",
                  background:"transparent", cursor:"pointer", transition:"background 0.2s",
                  height:"100%" }}
                  onMouseEnter={e => e.currentTarget.style.background = ts.dim}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <div style={{ ...mono, fontSize:10, letterSpacing:"0.18em",
                    textTransform:"uppercase", color:ts.color, marginBottom:10 }}>
                    Sector Tear Sheet
                  </div>
                  <div style={{ ...display, fontSize:22, color:C.white,
                    marginBottom:10, lineHeight:1.1 }}>{ts.title}</div>
                  <p style={{ ...body, fontSize:15, color:C.muted2, marginBottom:20 }}>{ts.desc}</p>
                  <div style={{ ...mono, fontSize:11, color:ts.color }}>
                    Open Tear Sheet →
                  </div>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ── FOOTER ────────────────────────────────────────────
function Footer({ section, goHome, goFinance, goRevenue, goTearSheets }) {
  const isMobile = useMobile();
  const color = section==="revenue"?C.blue:section==="tearsheets"?C.green:C.gold;
  const label = section==="revenue"?"J2 Summit Revenue":section==="finance"?"J2 Summit Finance":section==="tearsheets"?"J2 Summit Tear Sheets":"J2 Summit";
  return (
    <footer style={{ background:C.ink, borderTop:`1px solid ${C.border}`,
      padding:isMobile?"20px 20px":"24px 56px", display:"flex", alignItems:"center",
      justifyContent:"space-between", flexWrap:"wrap", gap:16 }}>
      <div style={{ ...display, fontSize:15, color }}>{label}</div>
      <div style={{ ...mono, fontSize:10, color:C.muted }}>© 2026 J2 Summit. All rights reserved.</div>
      <div style={{ display:"flex", gap:24 }}>
        {[["Home",goHome],["Finance",goFinance],["Revenue",goRevenue],["Tear Sheets",goTearSheets]].map(([l,fn])=>(
          <button key={l} onClick={fn} style={{ ...mono, fontSize:10, color:C.muted,
            background:"none", border:"none", cursor:"pointer",
            letterSpacing:"0.1em", textTransform:"uppercase" }}>{l}</button>
        ))}
        <a href="mailto:info@j2summit.com" style={{ ...mono, fontSize:10, color:C.muted,
          textDecoration:"none", letterSpacing:"0.1em", textTransform:"uppercase" }}>Email</a>
      </div>
    </footer>
  );
}

// ── APP ───────────────────────────────────────────────
export default function App() {
  const isMobile = useMobile();
  const [section, setSection] = useState("home");
  const [finTab,  setFinTab]  = useState("overview");
  const [revTab,  setRevTab]  = useState("overview");

  const goHome       = () => { setSection("home");       window.scrollTo({top:0}); };
  const goFinance    = () => { setSection("finance");    setFinTab("overview"); window.scrollTo({top:0}); };
  const goRevenue    = () => { setSection("revenue");    setRevTab("overview"); window.scrollTo({top:0}); };
  const goTearSheets = () => { setSection("finance"); setFinTab("tearsheets"); window.scrollTo({top:0}); };
  const setFTab   = (t) => { setFinTab(t); window.scrollTo({top:0}); };
  const setRTab   = (t) => { setRevTab(t); window.scrollTo({top:0}); };

  const finPanels = {
    overview:   <FinOverview setTab={setFTab}/>,
    services:   <FinServices/>,
    how:        <FinHow/>,
    about:      <FinAbout/>,
    contact:    <FinContact/>,
    tearsheets: <TearSheetsPage/>,
  };

  const revPanels = {
    overview: <RevOverview setTab={setRTab}/>,
    services: <RevServices/>,
    how:      <RevHow/>,
    contact:  <RevContact/>,
  };

  const hasSubTabs = section === "finance" || section === "revenue";

  return (
    <>
      <style>{`
        ${FONTS}
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        body{background:${C.ink};color:${C.white};font-family:'Outfit',sans-serif;
             -webkit-font-smoothing:antialiased;color-scheme:dark;overflow-x:hidden;}
        input,select,textarea{color-scheme:dark;}
        select option{background:${C.ink2};color:${C.white};}
        @keyframes marquee{from{transform:translateX(0);}to{transform:translateX(-50%);}}
        ::-webkit-scrollbar{width:6px;}
        ::-webkit-scrollbar-track{background:${C.ink};}
        ::-webkit-scrollbar-thumb{background:${C.border};}

        @media(max-width:768px){
          nav{padding:0 16px !important;gap:8px !important;}
          section{padding:56px 20px !important;}
          h1{font-size:clamp(32px,9vw,52px) !important;}
          .mob-grid{grid-template-columns:1fr !important;gap:20px !important;}
          footer{padding:20px 16px !important;flex-direction:column !important;gap:12px !important;}
        }
        @media(max-width:768px){
          *[style*="padding:96px 56px"]{padding:56px 20px !important;}
          *[style*="padding:100px 56px"]{padding:80px 20px 56px !important;}
          *[style*="padding:0 56px 88px"]{padding:0 20px 64px !important;}
          *[style*="gridTemplateColumns"]{grid-template-columns:1fr !important;}
          *[style*="grid-template-columns"]{grid-template-columns:1fr !important;}
          *[style*="gap:72"]{gap:32px !important;}
          *[style*="gap:64"]{gap:32px !important;}
          *[style*="gap:48"]{gap:24px !important;}
          *[style*="padding:0 48px"]{padding:0 16px !important;}
          *[style*="maxWidth:820"]{max-width:100% !important;}
          *[style*="maxWidth:800"]{max-width:100% !important;}
          *[style*="maxWidth:760"]{max-width:100% !important;}
          *[style*="maxWidth:540"]{max-width:100% !important;}
          *[style*="fontSize:20"]{font-size:16px !important;}
          *[style*="fontSize:18"]{font-size:15px !important;}
        }
      `}</style>

      <Nav section={section} goHome={goHome} goFinance={goFinance} goRevenue={goRevenue} goTearSheets={goTearSheets}/>

      {section === "finance" && <FinSubTabs tab={finTab} setTab={setFTab}/>}
      {section === "revenue" && <RevSubTabs tab={revTab} setTab={setRTab}/>}

      <main style={{ paddingTop: hasSubTabs ? (isMobile?144:102) : (isMobile?100:58), background:C.ink, minHeight:"100vh" }}>
        {section === "home"       && <HomePage goFinance={goFinance} goRevenue={goRevenue}/>}
        {section === "finance"    && (finPanels[finTab] || <FinOverview setTab={setFTab}/>)}
        {section === "revenue"    && (revPanels[revTab] || <RevOverview setTab={setRTab}/>)}
      </main>

      <Footer section={section} goHome={goHome} goFinance={goFinance} goRevenue={goRevenue} goTearSheets={goTearSheets}/>
    </>
  );
}
